import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/App";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import {
  LayoutDashboard, Users, Trophy, Target, DollarSign, Handshake, FileText,
  Video, FileCode, Settings, ChevronDown, Crown, Sparkles, BarChart3,
  Newspaper, ArrowLeftRight, Brain, FileBarChart, AlertTriangle, Swords,
  LayoutGrid, Lightbulb, Shield, Activity, UserSquare2, PhoneCall,
  BellRing, Scissors, Plus,
} from "lucide-react";
import NotificationCenter from "@/components/NotificationCenter";
import { AIChatPanel } from "@/components/layout/AIChatPanel";
import { SidebarNav } from "@/components/layout/SidebarNav";
import { SidebarUserProfile } from "@/components/layout/SidebarUserProfile";
import { MobileHeader } from "@/components/layout/MobileHeader";
import PushEnableBanner from "@/components/PushEnableBanner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MANAGER_ROLES = ["manager", "admin", "owner", "superadmin"];

// Pages a sales rep doesn't need cluttering their sidebar. Anything WITHOUT
// `roles` shows for everyone (reps + managers). Anything WITH `roles` is
// gated to that role set. Reps focus on the gamified sales surface
// (Leaderboard, Deals, EOD, Quotas, Commissions, Goals, Call Library, etc.).
// Iter176 — Rep UI Simplification. Sales reps (role: `rep`, `closer`,
// `setter`) see a stripped-down sidebar with ONLY the six items that
// touch their day-to-day work: their Dashboard (Closer/Setter), the
// Leaderboard, Deals, EOD, Quotas, Commissions. Everything else (Team
// admin, Analytics, Reports, Templates, Coaching library, etc.) is
// hidden. Marked with `repEssential: true` — the SidebarNav applies the
// filter based on the user's role.
const NAV_GROUPS = ["DASHBOARDS", "PIPELINE", "COACHING", "TEAM", "SETTINGS"];
// Iter185 (B01 + B02 — UX Audit Milestone 2). Sidebar cut from 23+ items
// to 8 top-level destinations (Home · Leaderboard · Deals · EOD · Commissions
// · Coaching · Team · Settings). Every "hidden" item stays reachable by
// direct URL and via in-page cross-links — we only remove them from the
// sidebar. Reps see a 4-item strip (Home · Leaderboard · EOD · Commissions
// + Deals as the practical fifth) so the sidebar stops feeling like an
// admin control-panel to someone who just wants to log activity.
const navItems = [
  // DASHBOARDS — collapses to one Home. Role-aware dispatch happens
  // inside /dashboard (Dashboard.jsx) with a manager "view-as" picker.
  { path: "/dashboard",        label: "Home",              icon: LayoutDashboard, group: "DASHBOARDS", repEssential: true },
  // Legacy dashboards are still reachable directly and via the view-as
  // picker on Home. Hidden from the sidebar to remove the "which
  // dashboard is mine" decision from the user.
  { path: "/my-dashboard",     label: "Manager Dashboard", icon: LayoutGrid,      roles: MANAGER_ROLES, group: "DASHBOARDS", hideInSidebar: true },
  { path: "/closer-dashboard", label: "Closer Dashboard",  icon: UserSquare2,     group: "DASHBOARDS", hideInSidebar: true },
  { path: "/setter-dashboard", label: "Setter Dashboard",  icon: PhoneCall,       group: "DASHBOARDS", hideInSidebar: true },
  { path: "/dialing-dashboard", label: "Dialing",          icon: PhoneCall,       group: "DASHBOARDS", hideInSidebar: true },
  { path: "/leaderboard",      label: "Leaderboard",       icon: Trophy,          group: "DASHBOARDS", repEssential: true },

  // PIPELINE — where deals live and reps report activity
  { path: "/deals",         label: "Deals",         icon: Handshake,   group: "PIPELINE", repEssential: true },
  { path: "/eod",           label: "EOD Reports",   icon: FileText,    group: "PIPELINE", repEssential: true },
  { path: "/quotas",        label: "Quotas",        icon: Target,      group: "PIPELINE", hideInSidebar: true },
  { path: "/commissions",   label: "Commissions",   icon: DollarSign,  group: "PIPELINE", repEssential: true },

  // COACHING — collapses to a single Coaching entry that opens a
  // tabbed page (Library / Coaching / Clips / Pre-call / Goals /
  // Challenges).
  { path: "/coaching",      label: "Coaching",      icon: Brain,       group: "COACHING" },
  { path: "/call-library",  label: "Call Library",  icon: Video,       group: "COACHING", hideInSidebar: true },
  { path: "/my-clips",      label: "My Clips",      icon: Scissors,    group: "COACHING", hideInSidebar: true },
  { path: "/precall-tips",  label: "Pre-call Tips", icon: Lightbulb,   group: "COACHING", hideInSidebar: true },
  { path: "/goals",         label: "Goals",         icon: Target,      group: "COACHING", hideInSidebar: true },
  { path: "/challenges",    label: "Challenges",    icon: Swords,      group: "COACHING", hideInSidebar: true },

  // TEAM — one entry that opens the team hub. Reports/Alerts/Analytics/
  // Weekly-Digest/Templates/Data-Health are reachable from inside it
  // AND directly by URL.
  { path: "/teams",         label: "Team",          icon: Users,           roles: MANAGER_ROLES, group: "TEAM" },
  { path: "/reports",       label: "Reports",       icon: FileBarChart,    group: "TEAM", hideInSidebar: true },
  { path: "/alerts",        label: "Alerts",        icon: AlertTriangle,   roles: MANAGER_ROLES, group: "TEAM", hideInSidebar: true },
  { path: "/analytics",     label: "Analytics",     icon: BarChart3,       roles: MANAGER_ROLES, group: "TEAM", hideInSidebar: true },
  { path: "/weekly-digest", label: "Weekly Digest", icon: Newspaper,       roles: MANAGER_ROLES, group: "TEAM", hideInSidebar: true },
  { path: "/templates",     label: "Templates",     icon: FileCode,        roles: MANAGER_ROLES, group: "TEAM", hideInSidebar: true },
  { path: "/data-health",   label: "Data Health",   icon: Activity,        roles: MANAGER_ROLES, group: "TEAM", hideInSidebar: true },

  // SETTINGS — Integrations + KOI Alerts fold under Settings.
  { path: "/settings",      label: "Settings",      icon: Settings,        group: "SETTINGS" },
  { path: "/koi-settings",  label: "KOI Alerts",    icon: BellRing,        group: "SETTINGS", hideInSidebar: true },
  { path: "/integrations",  label: "Integrations",  icon: ArrowLeftRight,  roles: MANAGER_ROLES, group: "SETTINGS", hideInSidebar: true },
];

export default function AppLayout({ children }) {
  const { user, logout, accounts, switchAccount, createAccount } = useAuth();
  const navigate = useNavigate();
  const [chatOpen, setChatOpen] = useState(false);
  const [theme, setTheme] = useState(() => localStorage.getItem("warmap-theme") || "dark");
  const [unreadCount, setUnreadCount] = useState(0);
  const [notifOpen, setNotifOpen] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [showNewAccountDialog, setShowNewAccountDialog] = useState(false);
  const [newAccountName, setNewAccountName] = useState("");
  const [creatingAccount, setCreatingAccount] = useState(false);
  const isManagerTier = MANAGER_ROLES.includes((user?.role || "").toLowerCase());

  const handleCreateAccount = async () => {
    if (!newAccountName.trim()) {
      toast.error("Business name is required");
      return;
    }
    setCreatingAccount(true);
    try {
      await createAccount(newAccountName.trim());
      // createAccount navigates to /onboarding on success.
    } catch (err) {
      toast.error(err.message || "Failed to create account");
      setCreatingAccount(false);
    }
  };

  const currentAccount = accounts?.find((a) => a.account_id === user?.account_id);

  useEffect(() => {
    document.documentElement.className = theme;
    localStorage.setItem("warmap-theme", theme);
  }, [theme]);

  const fetchUnreadCount = useCallback(async () => {
    try {
      const res = await fetch(`${API}/notifications/unread-count`, { credentials: "include" });
      if (res.ok) {
        const data = await res.json();
        setUnreadCount(data.count);
      }
    } catch (e) {
      console.error("Notification count fetch failed:", e);
    }
  }, []);

  useEffect(() => {
    fetchUnreadCount();
    const interval = setInterval(fetchUnreadCount, 30000);
    return () => clearInterval(interval);
  }, [fetchUnreadCount]);

  const toggleNotifs = () => setNotifOpen(!notifOpen);
  const toggleTheme = () => setTheme((t) => (t === "dark" ? "light" : "dark"));
  const closeSidebar = () => setSidebarOpen(false);

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  return (
    <div className={`flex min-h-screen ${theme === "light" ? "bg-gray-100" : "bg-[#0A0A0A]"}`}>
      {/* Mobile Header */}
      <MobileHeader
        user={user}
        theme={theme}
        unreadCount={unreadCount}
        onToggleNotifs={toggleNotifs}
        onOpenSidebar={() => setSidebarOpen(true)}
      />

      {/* Mobile Sidebar Overlay */}
      {sidebarOpen && (
        <div className="lg:hidden fixed inset-0 bg-black/50 z-50" onClick={closeSidebar} />
      )}

      {/* Sidebar */}
      <aside className={`fixed left-0 top-0 h-screen w-64 ${theme === "light" ? "bg-white border-gray-200" : "bg-[#0f0f10] border-white/5"} border-r flex flex-col z-50 transform transition-transform duration-300 ease-in-out ${sidebarOpen ? "translate-x-0" : "-translate-x-full"} lg:translate-x-0`}>
        {/* Logo & Dashboard Switcher */}
        <div className={`h-16 flex items-center justify-between px-4 border-b ${theme === "light" ? "border-gray-200" : "border-white/5"}`}>
          <div className="flex items-center gap-2">
            <Crown className="w-8 h-8 text-emerald-600" />
            <span className={`text-xl font-bold font-heading italic ${theme === "light" ? "text-gray-900" : "text-white"}`}>WarMap</span>
          </div>
          <button
            onClick={() => navigate("/marketing")}
            className={`p-2 rounded-lg transition-colors ${theme === "light" ? "hover:bg-gray-100 text-gray-600" : "hover:bg-white/10 text-gray-400"}`}
            title="Switch to Marketing Dashboard"
            data-testid="switch-to-marketing-btn"
          >
            <ArrowLeftRight className="w-5 h-5" />
          </button>
        </div>

        {/* Business Switcher */}
        <div className="px-3 py-2">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                data-testid="business-switcher"
                className={`w-full flex items-center gap-3 p-2 rounded-lg ${theme === "light" ? "bg-gray-100 hover:bg-gray-200" : "bg-white/5 hover:bg-white/10"} transition-colors`}
              >
                {currentAccount?.logo_url ? (
                  <img src={`${API.replace('/api', '')}${currentAccount.logo_url}`} alt="" className="w-10 h-10 rounded-full object-cover" />
                ) : (
                  <div className="w-10 h-10 rounded-full bg-emerald-600 flex items-center justify-center text-white font-bold text-sm">
                    {currentAccount?.name?.slice(0, 2).toUpperCase() || "LD"}
                  </div>
                )}
                <div className="flex-1 text-left">
                  <div className="text-xs text-gray-500">Business</div>
                  <div className={`text-sm truncate ${theme === "light" ? "text-gray-900" : "text-white"}`}>
                    {currentAccount?.business_name || currentAccount?.name || "Select Account"}
                  </div>
                </div>
                <ChevronDown className="w-4 h-4 text-gray-500" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className={`w-56 ${theme === "light" ? "bg-white border-gray-200" : "bg-[#1A1A1A] border-white/10"}`}>
              {accounts?.map((account) => (
                <DropdownMenuItem
                  key={account.account_id}
                  onClick={() => switchAccount(account.account_id)}
                  className={theme === "light" ? "text-gray-900 hover:bg-gray-100" : "text-white hover:bg-white/10"}
                >
                  {account.name}
                </DropdownMenuItem>
              ))}
              {isManagerTier && (
                <>
                  <DropdownMenuSeparator className={theme === "light" ? "bg-gray-200" : "bg-white/10"} />
                  <DropdownMenuItem
                    data-testid="new-account-menu-item"
                    onClick={() => setShowNewAccountDialog(true)}
                    className={`gap-2 ${theme === "light" ? "text-emerald-700 hover:bg-gray-100" : "text-emerald-400 hover:bg-white/10"}`}
                  >
                    <Plus className="w-4 h-4" /> New Account
                  </DropdownMenuItem>
                </>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        {/* Iter223 — create a new, empty business account under this email */}
        <Dialog open={showNewAccountDialog} onOpenChange={setShowNewAccountDialog}>
          <DialogContent className="bg-[#1A1A1A] border-white/10 text-white" data-testid="new-account-dialog">
            <DialogHeader>
              <DialogTitle>Create a New Account</DialogTitle>
              <DialogDescription className="text-gray-400">
                Spin up a brand-new, empty workspace under your current login.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-2 py-2">
              <Label className="text-gray-300">Business Name</Label>
              <Input
                autoFocus
                value={newAccountName}
                onChange={(e) => setNewAccountName(e.target.value.slice(0, 120))}
                placeholder="e.g. Day Trading Institute"
                maxLength={120}
                className="bg-white/5 border-white/10 text-white"
                data-testid="new-account-name-input"
                onKeyDown={(e) => { if (e.key === "Enter") handleCreateAccount(); }}
              />
              <p className="text-xs text-gray-500">This creates a brand-new, empty workspace — its own teams, EOD data, and leaderboard, separate from your other accounts.</p>
            </div>
            <DialogFooter>
              <Button
                data-testid="create-account-submit-btn"
                onClick={handleCreateAccount}
                disabled={creatingAccount}
                className="bg-gradient-to-r from-emerald-800 to-emerald-600"
              >
                {creatingAccount ? "Creating..." : "Create & Switch"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Navigation */}
        <SidebarNav navItems={navItems} user={user} theme={theme} onCloseSidebar={closeSidebar} />

        {/* User Profile */}
        <SidebarUserProfile user={user} theme={theme} onToggleTheme={toggleTheme} onLogout={handleLogout} />
      </aside>

      {/* Main Content */}
      <main
        // Iter174 — top padding now = mobile header (56px) + iOS safe-area (env inset).
        // Bottom uses safe-area-inset-bottom so content clears the home indicator
        // on iPhones and split-nav-bar Android devices.
        style={{
          paddingTop: "calc(3.5rem + env(safe-area-inset-top))",
          paddingBottom: "env(safe-area-inset-bottom)",
        }}
        className={`lg:ml-64 flex-1 min-h-screen lg:!pt-0 overflow-x-hidden ${theme === "light" ? "bg-gray-50 text-gray-900" : ""}`}
      >
        <PushEnableBanner />
        <div className="px-3 py-4 sm:p-4 md:p-6 lg:p-8 page-enter max-w-full overflow-x-hidden">{children}</div>
      </main>

      {/* Notification Center - Desktop Only */}
      <div className="hidden lg:block fixed top-4 right-24 z-50">
        <NotificationCenter />
      </div>

      {/* Super Admin Icon - Desktop Top Nav */}
      {user?.role === "superadmin" && (
        <button
          onClick={() => navigate("/superadmin/dashboard")}
          data-testid="topnav-superadmin-btn"
          className="hidden lg:flex fixed top-4 right-16 z-50 w-10 h-10 rounded-full bg-red-500/10 border border-red-500/20 items-center justify-center hover:bg-red-500/20 transition-colors"
          title="Super Admin Panel"
        >
          <Shield className="w-5 h-5 text-red-400" />
        </button>
      )}

      {/* Ask WarMap Button */}
      <button
        data-testid="ask-repvision-btn"
        onClick={() => setChatOpen(true)}
        className="ask-repvision-btn fixed right-4 bottom-20 lg:bottom-6 z-40 w-14 h-14 rounded-full flex items-center justify-center shadow-lg hover:scale-105 transition-transform"
        style={{ background: 'linear-gradient(135deg, #1a472a 0%, #2d5a3d 100%)' }}
      >
        <Sparkles className="w-6 h-6 text-white" />
      </button>

      {/* Chat Panel */}
      <AIChatPanel open={chatOpen} onOpenChange={setChatOpen} />
    </div>
  );
}
